import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { backendConfig } from "../config/backendConfig";
import KidBackground from "../ui/backgrounds/KidBackground";
import SmartImage from "../components/SmartImage";
import { getAvatarEmoji } from "../components/AvatarSelector";
import {
  coLearningGalleryPickerBackdropProps,
  coLearningInkBody,
  coLearningInkMuted,
  coLearningInkTitle,
} from "../ui/coLearningModalTheme";
import {
  ArrowLeft,
  Home,
  LayoutDashboard,
  Search,
  User,
  Palette,
  ArrowRight,
} from "lucide-react";
import { ARTIST_AGE_FILTER_OPTIONS, artistMatchesAgeFilter } from "../utils/artistAge";
import ArtistAgeFilterPills from "../components/ArtistAgeFilterPills";
import { artNavPillBtn } from "../ui/artmosphereButtonTheme";

const pillNavBtn = artNavPillBtn;

function ArtistCardAvatar({ artist }) {
  const name = artist.displayName || artist.name || "Artist";
  if (artist.avatarId) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] text-5xl">
        {getAvatarEmoji(artist.avatarId)}
      </div>
    );
  }
  if (artist.canDisplayProfilePic === true && artist.profilePicture) {
    return (
      <SmartImage
        src={artist.profilePicture}
        alt={name}
        className="h-full w-full object-cover"
        containerClassName="h-full w-full"
      />
    );
  }
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#fff9ef] to-[#ffefd6] px-4">
      <User className="h-12 w-12 text-[#c9a574]" strokeWidth={1.25} aria-hidden />
      <span className="text-center text-[11px] font-medium text-[#8A6A44]">Profile private</span>
    </div>
  );
}

export default function ArtistsProfiles() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [ageFilter, setAgeFilter] = useState(() => searchParams.get("age") || "all");

  useEffect(() => {
    const fromUrl = searchParams.get("search");
    if (fromUrl != null) setSearchTerm(fromUrl);
    const fromAge = searchParams.get("age");
    if (fromAge != null && ARTIST_AGE_FILTER_OPTIONS.some((o) => o.id === fromAge)) {
      setAgeFilter(fromAge);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const overallTimeout = setTimeout(() => setLoading(false), 10000);

        const profilesSnapshot = await getDocs(collection(db, "artistProfiles"));
        const profiles = profilesSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const artistsWithArtworks = (
          await Promise.allSettled(
            profiles.map(async (profile) => {
              try {
                const artworksPromise = Promise.race([
                  (async () => {
                    const artworksQuery = query(
                      collection(db, "artworks"),
                      where("uid", "==", profile.uid),
                      where("visibility", "==", "public")
                    );
                    const artworksSnapshot = await getDocs(artworksQuery);
                    return artworksSnapshot.docs.map((d) => ({
                      id: d.id,
                      ...d.data(),
                    }));
                  })(),
                  new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("Artworks query timeout")), 3000)
                  ),
                ]).catch(() => []);

                const consentPromise = Promise.race([
                  (async () => {
                    const backendUrl = backendConfig.baseUrl;
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 3000);
                    try {
                      const response = await fetch(
                        `${backendUrl}/api/consent/public-display/${profile.uid}?_t=${Date.now()}`,
                        {
                          signal: controller.signal,
                          cache: "no-store",
                          headers: { "Cache-Control": "no-cache" },
                        }
                      );
                      clearTimeout(timeoutId);
                      if (!response.ok) {
                        return { canDisplayName: true, canDisplayProfilePic: true };
                      }
                      return response.json();
                    } catch {
                      clearTimeout(timeoutId);
                      return { canDisplayName: true, canDisplayProfilePic: true };
                    }
                  })(),
                  new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("Consent API timeout")), 3500)
                  ),
                ]).catch(() => ({
                  canDisplayName: true,
                  canDisplayProfilePic: true,
                }));

                const [artworks, consentData] = await Promise.all([
                  artworksPromise,
                  consentPromise,
                ]);

                const canDisplayName = consentData.canDisplayName === true;
                const canDisplayProfilePic = consentData.canDisplayProfilePic === true;

                let displayName = profile.name || profile.nickname || "Artist";
                if (!canDisplayName) {
                  displayName = profile.nickname || profile.name || "Artist";
                }

                return {
                  ...profile,
                  displayName,
                  canDisplayProfilePic,
                  profilePicture:
                    canDisplayProfilePic === true ? profile.profilePicture : null,
                  artworks,
                  artworkCount: artworks.length,
                };
              } catch (error) {
                console.error(`Error processing profile ${profile.uid}:`, error);
                return null;
              }
            })
          )
        )
          .map((result) => (result.status === "fulfilled" ? result.value : null))
          .filter(Boolean);

        clearTimeout(overallTimeout);

        const sortedArtists = artistsWithArtworks.sort((a, b) => {
          if (b.artworkCount !== a.artworkCount) return b.artworkCount - a.artworkCount;
          return (a.displayName || "").localeCompare(b.displayName || "");
        });

        setArtists(sortedArtists);
      } catch (error) {
        console.error("Error fetching artists:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, []);

  const searchFiltered = artists.filter((artist) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (artist.displayName || artist.name || "").toLowerCase().includes(q) ||
      artist.bio?.toLowerCase().includes(q) ||
      artist.styles?.some((style) => style.toLowerCase().includes(q))
    );
  });

  const filteredArtists = searchFiltered.filter((artist) =>
    artistMatchesAgeFilter(artist, ageFilter)
  );

  const activeAgeLabel =
    ARTIST_AGE_FILTER_OPTIONS.find((o) => o.id === ageFilter)?.label || "All ages";

  const handleViewArtistGallery = (artist) => {
    navigate(`/artist-public-gallery/${artist.uid}`);
  };

  const shellStyle = {
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
  };

  if (loading) {
    return (
      <div className="relative flex min-h-screen flex-col">
        <KidBackground variant="aurora" intensity={0.38} className="z-0" />
        <div className="relative z-10 flex flex-1 items-center justify-center p-8">
          <div className="text-center">
            <div
              className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-[#F5D99B] border-t-[#B45309]"
              aria-hidden
            />
            <p className={`text-sm font-medium ${coLearningInkTitle}`}>Loading artists…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden">
      <KidBackground variant="aurora" intensity={0.38} className="z-0" />

      <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-[1400px] flex-1 flex-col px-2 py-2 md:px-4 md:py-4 lg:py-5">
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden md:rounded-[36px] md:border md:border-[#F6D57B]/80 md:shadow-[0_24px_70px_rgba(120,72,20,0.12)]"
          style={shellStyle}
        >
          <header className="relative z-30 flex-shrink-0 border-b border-[#F5D99B]/50 bg-[#FFFDF9]/80 px-4 py-2.5 backdrop-blur-md sm:px-6 md:px-8">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.28),transparent_70%)]"
              aria-hidden
            />
            <div className="relative flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <button type="button" onClick={() => navigate(-1)} className={pillNavBtn} title="Back">
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span>Back</span>
                </button>
                <div className="min-w-0">
                  <h1 className="font-serif text-lg font-semibold leading-tight text-[#2F251B] sm:text-xl md:text-2xl">
                    Artmosphere Artists
                  </h1>
                  <p className="mt-0.5 text-xs leading-snug text-[#6B4E32] sm:text-[13px]">
                    Young creators sharing their art with the world.
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => navigate("/my-art-world")}
                  className={pillNavBtn}
                  title="Artist Corner"
                >
                  <LayoutDashboard className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Artist Corner</span>
                </button>
                <button type="button" onClick={() => navigate("/home")} className={pillNavBtn} title="Home">
                  <Home className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span>Home</span>
                </button>
              </div>
            </div>
          </header>

          <div className="flex-shrink-0 border-b border-[#F5D99B]/40 px-3 py-3 sm:px-6 md:px-8">
            <p className={`mx-auto mb-3 max-w-3xl text-center text-sm ${coLearningInkMuted}`}>
              Discover the community of young artists on Artmosphere — each with their own style
              and story.
            </p>
            <div className="relative mx-auto max-w-xl">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6A44]"
                aria-hidden
              />
              <input
                type="search"
                placeholder="Search by name, bio, or art style…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-full border border-[#F5D99B] bg-white/90 py-2.5 pl-10 pr-4 text-sm text-[#2F251B] shadow-sm placeholder:text-[#8A6A44]/60 focus:border-[#E8C97A] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/25"
                aria-label="Search artists"
              />
            </div>
            <ArtistAgeFilterPills
              value={ageFilter}
              onChange={setAgeFilter}
              className="mx-auto mt-3 max-w-3xl"
            />
            <p className="mt-2 text-center text-[11px] font-medium text-[#8A6A44]">
              {filteredArtists.length} artist{filteredArtists.length === 1 ? "" : "s"}
              {ageFilter !== "all" ? ` · ${activeAgeLabel}` : ""}
              {searchTerm.trim() ? ` · matching “${searchTerm.trim()}”` : ""}
            </p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 sm:px-6 md:px-8 md:py-6">
            {filteredArtists.length === 0 ? (
              <div className="mx-auto max-w-md rounded-2xl border border-dashed border-[#F5D99B] bg-white/60 px-6 py-14 text-center">
                <p className="text-4xl mb-3" aria-hidden>
                  🎨
                </p>
                <h3 className={`text-lg font-semibold ${coLearningInkTitle}`}>
                  {searchTerm || ageFilter !== "all" ? "No artists found" : "No artists yet"}
                </h3>
                <p className={`mt-2 text-sm ${coLearningInkMuted}`}>
                  {searchTerm && ageFilter !== "all"
                    ? "Try a different search or age range."
                    : searchTerm
                      ? "Try a different search term."
                      : ageFilter !== "all"
                        ? "No artists in this age range yet."
                        : "Be the first to join the creative community!"}
                </p>
              </div>
            ) : (
              <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                {filteredArtists.map((artist) => (
                  <article
                    key={artist.uid}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-[#F6D57B]/60 bg-white/[0.92] shadow-[0_8px_24px_rgba(120,72,20,0.07)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(120,72,20,0.12)]"
                  >
                    <button
                      type="button"
                      className="relative h-44 w-full overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]/50"
                      onClick={() => setSelectedArtist(artist)}
                    >
                      <ArtistCardAvatar artist={artist} />
                      <span className="absolute right-2 top-2 rounded-full border border-white/80 bg-[#FFF9EF]/95 px-2 py-0.5 text-[10px] font-semibold text-[#92400e] shadow-sm backdrop-blur-sm">
                        {artist.artworkCount}{" "}
                        {artist.artworkCount === 1 ? "artwork" : "artworks"}
                      </span>
                    </button>

                    <div className="flex flex-1 flex-col p-4">
                      <h3 className={`truncate text-lg font-semibold ${coLearningInkTitle}`}>
                        {artist.displayName || artist.name || "Artist"}
                      </h3>
                      {artist.bio && (
                        <p className={`mt-1 line-clamp-2 text-sm ${coLearningInkMuted}`}>{artist.bio}</p>
                      )}
                      {artist.styles?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {artist.styles.slice(0, 3).map((style) => (
                            <span
                              key={style}
                              className="inline-flex items-center gap-1 rounded-full border border-[#F5D99B]/80 bg-[#FFF9EF] px-2 py-0.5 text-[10px] font-medium text-[#8A6A44]"
                            >
                              <Palette className="h-2.5 w-2.5 opacity-70" aria-hidden />
                              {style}
                            </span>
                          ))}
                        </div>
                      )}
                      {artist.artworkCount > 0 && (
                        <button
                          type="button"
                          onClick={() => handleViewArtistGallery(artist)}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                        >
                          View Gallery
                          <ArrowRight className="h-4 w-4 opacity-90" aria-hidden />
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedArtist && (
        <div
          {...coLearningGalleryPickerBackdropProps}
          onClick={() => setSelectedArtist(null)}
        >
          <div
            className="relative mx-auto max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[#e8e0d4] bg-white/95 p-5 shadow-2xl backdrop-blur-xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedArtist(null)}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-[#e8e0d4] bg-white text-xl leading-none text-[#141210] hover:bg-stone-50"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className={`pr-10 text-2xl ${coLearningInkTitle}`}>
              {selectedArtist.displayName || selectedArtist.name || "Artist"}
            </h2>

            <div className="mt-4 overflow-hidden rounded-xl border border-[#F5D99B]/50">
              <div className="h-48">
                <ArtistCardAvatar artist={selectedArtist} />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {selectedArtist.bio && (
                <div>
                  <h4 className={`text-sm font-semibold ${coLearningInkTitle}`}>About</h4>
                  <p className={`mt-1 text-sm ${coLearningInkBody}`}>{selectedArtist.bio}</p>
                </div>
              )}
              {selectedArtist.styles?.length > 0 && (
                <div>
                  <h4 className={`text-sm font-semibold ${coLearningInkTitle}`}>Art styles</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedArtist.styles.map((style) => (
                      <span
                        key={style}
                        className="rounded-full border border-[#F5D99B] bg-[#FFF9EF] px-3 py-1 text-xs font-medium text-[#8A6A44]"
                      >
                        {style}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {selectedArtist.artworks?.length > 0 && (
              <div className="mt-5">
                <h4 className={`mb-3 text-sm font-semibold ${coLearningInkTitle}`}>
                  Public artworks ({selectedArtist.artworkCount})
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {selectedArtist.artworks.slice(0, 6).map((artwork) => (
                    <div key={artwork.id} className="aspect-square overflow-hidden rounded-lg border border-[#e8e0d4]">
                      <SmartImage
                        src={artwork.imageUrl}
                        alt={artwork.title || "Artwork"}
                        className="h-full w-full object-cover"
                        containerClassName="h-full w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedArtist.artworkCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedArtist(null);
                  handleViewArtistGallery(selectedArtist);
                }}
                className="mt-6 w-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5"
              >
                View full gallery
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
